import { Modes } from '@/models/modes';
import { mapToSearchParameters } from '@/models/search-filters';
import { tendersService } from '@/services/tenders.service';
import { userProfile } from '@/store/user-profile.module';
import axios from 'axios';

const initialState = () => ({
  tenders: [],
  matchesCount: 0,
  exploreCount: 0,
  pagination: {
    page: 1,
    nextPage: null,
    previousPage: null,
    totalCount: 0,
    totalPages: 0,
  },
  nextTenders: null,
  previousTenders: null,
  bufferTender: null,
  // Phones show one growing list instead of pages: the next page is added below the
  // loaded ones, both by "load more" and by the tender page's next/previous navigation.
  continuous: false,
  loadingMore: false,
  loadMoreError: null,
  sortingField: 'publishedDate',
  sortingOrder: 'desc',
  status: {
    error: null,
    matchesError: null,
    pending: true,
    resolved: false,
    rejected: false,
    matchesPending: true,
  },
});

// A tender published between two loads shifts the pages by one, so the next page
// starts with a tender already listed.
const withoutListed = (listed, tenders) => {
  const ids = new Set(listed.map((tender) => tender.id));
  return tenders.filter((tender) => !ids.has(tender.id));
};

// Keyword coverage and linked opportunities, loaded after a views-based search.
// `prefetch` leaves the listed tenders' keywords on screen while a new page's arrive.
const loadSearchFollowUps = ({ commit, dispatch, rootGetters }, tenders, prefetch) => {
  // Pass the working view filters so coverage can fall back to all tenant keywords
  // when the view itself has no keywordIds / allKeywords.
  dispatch('getTendersMatches', {
    tenders,
    searchFilters: rootGetters['searchFilters/searchFilters'] || {},
    ...(prefetch ? { prefetch } : {}),
  });

  dispatch('opportunities/getOpportunitiesByTenders', { tenderIds: tenders.map((t) => t.id) }, { root: true }).then(
    (opportunities) => {
      commit('SET_TENDERS_OPPORTUNITIES', opportunities);
    },
  );
};

const state = initialState();

const actions = {
  getMatchesCount({ commit }) {
    tendersService.getMatchesCount().then((count) => commit('SET_MATCHES_COUNT', count));
  },
  getTendersByMode({ commit, dispatch }, { searchFilters, mode, page }) {
    commit('GET_TENDERS');

    const sortingField = state.sortingField;
    const sortingOrder = state.sortingOrder;
    const parameters = mapToSearchParameters(searchFilters, mode);

    if (mode === Modes.EXPLORE) dispatch('getExploreCount');

    return tendersService.getTendersByMode({ parameters, mode: mode.apiCall, page, sortingField, sortingOrder }).then(
      ({ tenders, pagination }) => {
        commit('SET_TENDERS', { tenders, pagination });
        if (tenders.length === 0) return;

        dispatch('getTendersMatches', {
          tenders: tenders,
          searchFilters: searchFilters,
          mode,
        });

        dispatch(
          'opportunities/getOpportunitiesByTenders',
          { tenderIds: tenders.map((t) => t.id) },
          { root: true },
        ).then((opportunities) => {
          commit('SET_TENDERS_OPPORTUNITIES', opportunities);
        });
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  getExploreCount({ commit }) {
    tendersService.getExploreCount().then((count) => commit('SET_EXPLORE_COUNT', count));
  },
  // `append` adds the page below the listed tenders (the phone list's "load more").
  refreshTenders(
    { commit, dispatch, rootState },
    { searchFilters, page, mode = Modes[`${rootState.modes.mode}`], append = false },
  ) {
    commit(append ? 'LOAD_MORE_TENDERS' : 'REFRESH_TENDERS');

    const sortingField = state.sortingField;
    const sortingOrder = state.sortingOrder;
    const parameters = mapToSearchParameters(searchFilters, mode);

    return tendersService.getTendersByMode({ parameters, mode: mode.apiCall, page, sortingField, sortingOrder }).then(
      ({ tenders, pagination }) => {
        commit(append ? 'APPEND_TENDERS' : 'SET_TENDERS', { tenders, pagination });
        if (tenders.length === 0) return;

        dispatch('getTendersMatches', {
          tenders: tenders,
          searchFilters: searchFilters,
          mode,
          // Leaves the listed tenders' keywords on screen while the new page's arrive.
          ...(append ? { prefetch: true } : {}),
        });

        dispatch(
          'opportunities/getOpportunitiesByTenders',
          { tenderIds: tenders.map((t) => t.id) },
          { root: true },
        ).then((opportunities) => {
          commit('SET_TENDERS_OPPORTUNITIES', opportunities);
        });
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit(append ? 'LOAD_MORE_ERROR' : 'ERROR', error);
          throw error;
        }
        // Another search took its place; the list asks again once in view.
        if (append) commit('LOAD_MORE_CANCELLED');
      },
    );
  },
  // Views-based search: run a saved view by id, or the live/working filters (raw view blob).
  // `append` adds the page below the listed tenders (the phone list's "load more").
  searchTenders({ commit, dispatch, rootGetters }, { viewId, filters, page = 1, pageSize = 25, append = false }) {
    commit(append ? 'LOAD_MORE_TENDERS' : 'REFRESH_TENDERS');

    const sortingField = state.sortingField;
    const sortingOrder = state.sortingOrder;

    return tendersService.searchTenders({ viewId, filters, page, pageSize, sortingField, sortingOrder }).then(
      ({ tenders, pagination }) => {
        commit(append ? 'APPEND_TENDERS' : 'SET_TENDERS', { tenders, pagination });
        if (tenders.length === 0) return;

        loadSearchFollowUps({ commit, dispatch, rootGetters }, tenders, append);
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit(append ? 'LOAD_MORE_ERROR' : 'ERROR', error);
          throw error;
        }
        // Another search took its place; the list asks again once in view.
        if (append) commit('LOAD_MORE_CANCELLED');
      },
    );
  },
  // Reloads every page the phone list shows, so a refresh keeps the reader's place in it.
  // One page at a time: each search request cancels the one in flight.
  async searchTendersThrough({ commit, dispatch, rootGetters }, { viewId, filters, lastPage, pageSize = 25 }) {
    commit('REFRESH_TENDERS');

    const sortingField = state.sortingField;
    const sortingOrder = state.sortingOrder;
    let tenders = [];
    let firstPagination = null;
    let pagination = null;

    try {
      for (let page = 1; page <= lastPage; page += 1) {
        const result = await tendersService.searchTenders({
          viewId,
          filters,
          page,
          pageSize,
          sortingField,
          sortingOrder,
        });
        tenders = tenders.concat(withoutListed(tenders, result.tenders));
        firstPagination = firstPagination || result.pagination;
        pagination = result.pagination;
        if (!result.pagination.nextPage) break;
      }
    } catch (error) {
      if (axios.isCancel(error)) return;
      commit('ERROR', error);
      throw error;
    }

    commit('SET_TENDERS', { tenders, pagination: { ...pagination, previousPage: firstPagination.previousPage } });
    if (tenders.length > 0) loadSearchFollowUps({ commit, dispatch, rootGetters }, tenders, false);
  },
  setContinuous({ commit }, continuous) {
    commit('SET_CONTINUOUS', continuous);
  },
  loadNextTenders({ commit, dispatch, rootState }, { searchFilters, page }) {
    const sortingField = state.sortingField;
    const sortingOrder = state.sortingOrder;
    const mode = Modes[`${rootState.modes.mode}`];
    const parameters = mapToSearchParameters(searchFilters, mode);

    return tendersService.getTendersByMode({ parameters, mode: mode.apiCall, page, sortingField, sortingOrder }).then(
      ({ tenders, pagination }) => {
        commit('LOAD_NEXT_TENDERS_SUCCESS', { tenders, pagination });
        if (tenders.length === 0) return;

        // Prefetch coverage onto the adjacent page only. Do not mark the visible
        // list as pending — that would look like a recalculation on return.
        dispatch('getTendersMatches', {
          tenders: tenders,
          searchFilters: searchFilters,
          mode,
          prefetch: true,
        });

        dispatch(
          'opportunities/getOpportunitiesByTenders',
          { tenderIds: tenders.map((t) => t.id) },
          { root: true },
        ).then((opportunities) => {
          commit('SET_TENDERS_OPPORTUNITIES', opportunities);
        });
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
        }
      },
    );
  },
  loadPreviousTenders({ commit, dispatch, rootState }, { searchFilters, page }) {
    const sortingField = state.sortingField;
    const sortingOrder = state.sortingOrder;
    const mode = Modes[`${rootState.modes.mode}`];
    const parameters = mapToSearchParameters(searchFilters, mode);

    return tendersService.getTendersByMode({ parameters, mode: mode.apiCall, page, sortingField, sortingOrder }).then(
      ({ tenders, pagination }) => {
        commit('LOAD_PREVIOUS_TENDERS_SUCCESS', { tenders, pagination });
        if (tenders.length === 0) return;

        dispatch('getTendersMatches', {
          tenders: tenders,
          searchFilters: searchFilters,
          mode,
          prefetch: true,
        });

        dispatch(
          'opportunities/getOpportunitiesByTenders',
          { tenderIds: tenders.map((t) => t.id) },
          { root: true },
        ).then((opportunities) => {
          commit('SET_TENDERS_OPPORTUNITIES', opportunities);
        });
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
        }
      },
    );
  },
  changePage({ commit }, { page }) {
    commit('CHANGE_PAGE_TENDERS', page);
  },
  readMany({ commit }, { ids }) {
    return tendersService.readMany(ids).then((userTenders) => commit('SET_USER_TENDERS', userTenders));
  },
  unreadMany({ commit }, { ids }) {
    return tendersService.unreadMany(ids).then((userTenders) => commit('SET_USER_TENDERS', userTenders));
  },
  followMany({ commit }, { ids }) {
    return tendersService.followMany(ids).then((userTenders) => commit('SET_USER_TENDERS', userTenders));
  },
  unfollowMany({ commit }, { ids }) {
    return tendersService.unfollowMany(ids).then((userTenders) => commit('SET_USER_TENDERS', userTenders));
  },
  shareMany({ commit }, { ids }) {
    return tendersService.shareMany(ids).then((tenantTenders) => commit('SET_TENANT_TENDERS', tenantTenders));
  },
  unshareMany({ commit }, { ids }) {
    return tendersService.unshareMany(ids).then((tenantTenders) => commit('SET_TENANT_TENDERS', tenantTenders));
  },
  // TODO: TENANT_TENDERS ?
  archiveMany({ commit }, { ids }) {
    return tendersService.archiveMany(ids).then((userTenders) => commit('SET_USER_TENDERS', userTenders));
  },
  unarchiveMany({ commit }, { ids }) {
    return tendersService.unarchiveMany(ids).then((userTenders) => commit('SET_USER_TENDERS', userTenders));
  },
  rateUpMany({ commit }, { ids }) {
    return tendersService.rateMany({ tenderIds: ids, rating: 'UP' }).then((tenantTenders) => {
      commit('SET_TENANT_TENDERS', tenantTenders);
    });
  },
  rateDownMany({ commit }, { ids }) {
    return tendersService
      .rateMany({ tenderIds: ids, rating: 'DOWN' })
      .then((tenantTenders) => commit('SET_TENANT_TENDERS', tenantTenders));
  },
  setCustomSortingField({ commit }, { sortingField }) {
    commit('SET_CUSTOM_SORTING_FIELD', sortingField);
  },
  setCustomSortingOrder({ commit }, { sortingOrder }) {
    commit('SET_CUSTOM_SORTING_ORDER', sortingOrder);
  },

  // UTIL FUNCTION
  // Explore: tenant coverage (empty keywords) + search-bar highlights when keywords are entered.
  // Views/inbox: empty keywords → tenant coverage. A view with no keyword filter still needs
  // that coverage so the table can show whether any tenant keyword would have matched.
  getTendersMatches({ commit, dispatch, rootGetters }, { tenders, searchFilters = {}, mode, prefetch = false }) {
    if (!prefetch) commit('SET_MATCHED_KEYWORDS');
    const ids = tenders.map((t) => t.id);

    const applyMatches = (request) =>
      request.then(
        (tendersMatches) => {
          commit('SET_TENDERS_MATCHED_KEYWORDS', { tendersMatches, tenderIds: ids, prefetch });
        },
        (error) => {
          if (!axios.isCancel(error) && !prefetch) {
            commit('ERROR_MATCHES', error);
          }
        },
      );

    if (mode === Modes.EXPLORE) {
      return applyMatches(
        tendersService.getTendersMatchesForExplore({
          tenderIds: ids,
          searchKeywords: searchFilters.keywords,
          silent: prefetch,
        }),
      );
    }

    const keywordIds = searchFilters.keywordIds || [];
    const hasKeywordFilter = keywordIds.length > 0 || !!searchFilters.allKeywords;

    // View has keyword filters (or allKeywords): empty keywords → API uses all tenant keywords.
    if (hasKeywordFilter) {
      return applyMatches(tendersService.getTendersMatches({ tenderIds: ids, keywords: [], silent: prefetch }));
    }

    // View has no keyword filter: still resolve tenant keyword values and match against them
    // so coverage is computed even when the API does not apply the empty-array default.
    const tenantKeywords = rootGetters['keywords/keywords'] || [];
    const ready = tenantKeywords.length ? Promise.resolve() : dispatch('keywords/getKeywords', null, { root: true });

    return ready.then(() => {
      const keywords = (rootGetters['keywords/keywords'] || []).map((keyword) => keyword.value);
      return applyMatches(
        tendersService.getTendersMatches({
          tenderIds: ids,
          keywords: keywords.length ? keywords : [],
          silent: prefetch,
        }),
      );
    });
  },
};

const mutations = {
  GET_TENDERS(state) {
    state.status = {
      ...state.status,
      error: null,
      matchesError: null,
      pending: true,
      matchesPending: true,
      resolved: false,
      rejected: false,
    };
    state.tenders = [];
    state.loadingMore = false;
    state.loadMoreError = null;
  },
  SET_MATCHES_COUNT(state, count) {
    state.matchesCount = count;
  },
  SET_EXPLORE_COUNT(state, count) {
    state.exploreCount = count;
  },
  REFRESH_TENDERS(state) {
    state.status = {
      ...state.status,
      error: null,
      matchesError: null,
      pending: true,
      matchesPending: true,
      resolved: false,
      rejected: false,
    };
    // The new search cancels a "load more" in flight.
    state.loadingMore = false;
    state.loadMoreError = null;
  },
  LOAD_MORE_TENDERS(state) {
    state.loadingMore = true;
    state.loadMoreError = null;
  },
  APPEND_TENDERS(state, { tenders, pagination }) {
    state.loadingMore = false;
    state.tenders = state.tenders.concat(withoutListed(state.tenders, tenders));
    // The list still starts where it did, so there is still nothing before it.
    state.pagination = { ...pagination, previousPage: state.pagination.previousPage };
    state.nextTenders = null;
    state.previousTenders = null;
    state.bufferTender = null;
  },
  LOAD_MORE_ERROR(state, error) {
    state.loadingMore = false;
    state.loadMoreError = error;
  },
  LOAD_MORE_CANCELLED(state) {
    state.loadingMore = false;
  },
  SET_CONTINUOUS(state, continuous) {
    state.continuous = continuous;
  },
  LOAD_NEXT_TENDERS_SUCCESS(state, { tenders, pagination }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.nextTenders = {
      tenders: tenders,
      pagination: pagination,
    };
    state.bufferTender = tenders[0];
  },
  LOAD_PREVIOUS_TENDERS_SUCCESS(state, { tenders, pagination }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.previousTenders = {
      tenders: tenders,
      pagination: pagination,
    };
    state.bufferTender = tenders[tenders.length - 1];
  },
  CHANGE_PAGE_TENDERS(state, page) {
    if (page > state.pagination.page && state.continuous) {
      state.tenders = state.tenders.concat(withoutListed(state.tenders, state.nextTenders.tenders));
      state.pagination = { ...state.nextTenders.pagination, previousPage: state.pagination.previousPage };
      state.bufferTender = null;
      state.nextTenders = null;
    } else if (page > state.pagination.page) {
      state.previousTenders = {
        tenders: state.tenders,
        pagination: state.pagination,
      };
      state.tenders = state.nextTenders.tenders;
      state.pagination = state.nextTenders.pagination;
      state.bufferTender = state.previousTenders.tenders[state.previousTenders.tenders.length - 1];
      state.nextTenders = null;
    } else {
      state.nextTenders = {
        tenders: state.tenders,
        pagination: state.pagination,
      };
      state.tenders = state.previousTenders.tenders;
      state.pagination = state.previousTenders.pagination;
      state.bufferTender = state.nextTenders.tenders[0];
      state.previousTenders = null;
    }
  },
  SET_TENDERS(state, { tenders, pagination }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
      ...(tenders.length === 0 ? { matchesPending: false } : {}),
    };
    state.tenders = tenders;
    state.pagination = pagination;
    state.nextTenders = null;
    state.previousTenders = null;
  },
  SET_MATCHED_KEYWORDS(state) {
    state.status = {
      ...state.status,
      matchesError: null,
      matchesPending: true,
    };
  },
  SET_TENDERS_MATCHED_KEYWORDS(state, { tendersMatches = {}, tenderIds, prefetch = false } = {}) {
    if (!prefetch) {
      state.status = { ...state.status, matchesError: null, matchesPending: false };
    }

    const matchesById = Array.isArray(tendersMatches)
      ? tendersMatches.reduce((acc, match) => {
          const id = match.tenderId || match.id;
          if (id) acc[id] = match;
          return acc;
        }, {})
      : tendersMatches || {};

    const requestedIds = new Set((tenderIds || []).map(String));

    const applyMatches = (tenders) => {
      if (!tenders || tenders.length === 0) return;
      tenders.forEach((tender, index) => {
        if (requestedIds.size > 0 && !requestedIds.has(String(tender.id))) return;

        const match = matchesById[tender.id] || matchesById[String(tender.id)];
        const next = {
          ...tender,
          matchedKeywords: match ? match.matchedKeywords || [] : [],
        };
        if (match && match.highlights && match.highlights.title) next.title = match.highlights.title;
        tenders.splice(index, 1, next);
      });
    };

    applyMatches(state.tenders);
    if (state.previousTenders) applyMatches(state.previousTenders.tenders);
    if (state.nextTenders) applyMatches(state.nextTenders.tenders);
  },
  SET_TENDERS_OPPORTUNITIES(state, opportunities) {
    if (!opportunities || opportunities.length === 0) return;

    const updateTenderOpportunities = (tenders) => {
      if (!tenders || tenders.length === 0) return;
      tenders.forEach((tender) => {
        // Only add the opportunities to the tender if they are not already there
        let oppIds = opportunities.filter((opportunity) => opportunity.tenderId === tender.id);
        tender.opportunities = Array.from(new Set(tender.opportunities.concat(oppIds)));
      });
    };

    updateTenderOpportunities(state.tenders);
    if (state.previousTenders) updateTenderOpportunities(state.previousTenders.tenders);
    if (state.nextTenders) updateTenderOpportunities(state.nextTenders.tenders);
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      matchesPending: false,
      rejected: true,
      error: error,
    };
  },
  ERROR_MATCHES(state, error) {
    state.status = {
      ...state.status,
      matchesError: error,
      matchesPending: false,
    };
  },
  SET_CUSTOM_SORTING_FIELD(state, sortingField) {
    state.sortingField = sortingField;
  },
  SET_CUSTOM_SORTING_ORDER(state, sortingOrder) {
    state.sortingOrder = sortingOrder;
  },
  SET_USER_TENDER(state, userTender) {
    const index = state.tenders.findIndex((tender) => tender.id === userTender.tenderId);
    if (index !== -1) state.tenders[index].userTender = userTender;
  },
  SET_USER_TENDERS(state, userTenders) {
    userTenders.forEach((userTender) => {
      const index = state.tenders.findIndex((tender) => tender.id === userTender.tenderId);
      if (index !== -1) state.tenders[index].userTender = userTender;
    });
  },
  SET_TENANT_TENDER(state, tenantTender) {
    const index = state.tenders.findIndex((tender) => tender.id === tenantTender.tenderId);
    if (index !== -1) state.tenders[index].tenantTender = tenantTender;
  },
  SET_TENANT_TENDERS(state, tenantTenders) {
    tenantTenders.forEach((tenantTender) => {
      const index = state.tenders.findIndex((tender) => tender.id === tenantTender.tenderId);
      if (index !== -1) state.tenders[index].tenantTender = tenantTender;
    });
  },

  // Mutations called from other modules
  UPDATE_ASSIGNEES(state, { tenderId, assignees }) {
    const index = state.tenders.findIndex((tender) => tender.id === tenderId);
    if (index !== -1) state.tenders[index].assignees = assignees;
  },
  SUCCESS_ADDING_OPPORTUNITY(state, { tenderId, opportunity }) {
    const index = state.tenders.findIndex((tender) => tender.id === tenderId);
    if (index !== -1) state.tenders[index].opportunities.push(opportunity);
  },
  SUCCESS_ADDING_OPPORTUNITIES(state, opportunities) {
    opportunities.forEach((opportunity) => {
      const index = state.tenders.findIndex((t) => t.id === opportunity.tenderId);
      if (index >= 0) {
        state.tenders[index].opportunities.push(opportunity);
      }
    });
  },
  SUCCESS_DELETING_OPPORTUNITY(state, { tenderId, opportunity }) {
    if (state.tenders.length > 0) {
      const tenderIndex = state.tenders.findIndex((tender) => tender.id === tenderId);
      if (tenderIndex >= 0) {
        const oppIndex = state.tenders[tenderIndex].opportunities.findIndex((opp) => opp.id === opportunity.id);
        if (oppIndex !== -1) state.tenders[tenderIndex].opportunities.splice(oppIndex, 1);
      }
    }
  },
  SUCCESS_DELETING_OPPORTUNITIES(state, tenderIds) {
    if (state.tenders.length > 0) {
      state.tenders = state.tenders.filter((tender) => !tenderIds.includes(tender.id));
    }
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  tenders: (state) => state.tenders,
  matchesCount: (state) => state.matchesCount,
  exploreCount: (state) => state.exploreCount,
  bufferTender: (state) => state.bufferTender,
  previousTenders: (state) => state.previousTenders,
  nextTenders: (state) => state.nextTenders,
  getPreviousTender: (_, getters) => (id) => {
    const currentTenderIndex = getters.tenders.findIndex((tender) => tender.id === id);
    return currentTenderIndex === -1 ? undefined : getters.tenders[currentTenderIndex - 1];
  },
  getNextTender: (_, getters) => (id) => {
    const currentTenderIndex = getters.tenders.findIndex((tender) => tender.id === id);
    return currentTenderIndex === -1 ? undefined : getters.tenders[currentTenderIndex + 1];
  },

  continuous: (state) => state.continuous,
  loadingMore: (state) => state.loadingMore,
  loadMoreError: (state) => state.loadMoreError,
  sortingField: (state) => state.sortingField,
  sortingOrder: (state) => state.sortingOrder,

  // Pagination
  page: (state) => state.pagination.page,
  totalCount: (state) => state.pagination.totalCount,
  totalPages: (state) => state.pagination.totalPages,
  nextPage: (state) => new URLSearchParams(state.pagination.nextPage).get('page'),
  previousPage: (state) => new URLSearchParams(state.pagination.previousPage).get('page'),

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
  matchesPending: (state) => state.status.matchesPending,
};

export const tenders = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
  modules: {
    userProfile,
  },
};
